import { dynamicParameter } from '@/mixins/service/mixin-service';
import cloneDeep from 'lodash/cloneDeep'
import dateFormatter from '@/services/formatter_services/date-formatter';
import get from 'lodash/get';
import restUtils from '@/utils/rest-utils.js';
import axios from 'axios';

function needsEscaping(value) {
  return value.includes(';') || // Since this is the delimiter (commas are fine)
    value.includes('"') ||      // Quote delimiter
    value.includes('\n') ||     // Line feed
    value.includes('\r');       // Carriage return
}

function exportToCsv(data, schema, filename) {
  if (!data.length || !schema.length) return;

  const headers = schema.map(col => col.column);

  const rows = data.map(item => {
    return schema.map(col => {
      const value = col.value(item);

      if (value === null || value === undefined) {
        return '';
      }

      const stringValue = String(value);

      if (needsEscaping(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    });
  });

  const csvContent = [
    headers.join(';'), // use semicolon as delimiter
    ...rows.map(row => row.join(';'))
  ].join('\n');

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export default function (notVisibleHeaders = [], bulkIds = [], excludeIds = []) {
  const getNotVisibleHeaders = dynamicParameter(notVisibleHeaders);
  const getBulkIds = dynamicParameter(bulkIds);
  const getExcludeIds = dynamicParameter(excludeIds);

  return {
    props: {
      excelObjectDataPath: { type: String, default: 'data.' },
    },
    data() {
      return {
        filename: undefined,
        csvExportLoading: false,
      }
    },
    methods: {
      deepGet(obj, path) {
        return get(obj, path) ?? get(obj?.data, path);
      },
      exportCsvData() {
        this.csvExportLoading = true;
        let schema = [];
        let responseDataPath = this.excelObjectDataPath ?? '';

        const clonedHeaders = cloneDeep(this.internalHeaders ?? this.headers);

        const sortedHeaders = clonedHeaders.sort((header1, header2) => {
          const positionFilter1 = this.orderedHeaders.findIndex(
            (_value) => _value === header1.value
          );
          const positionFilter2 = this.orderedHeaders.findIndex(
            (_value) => _value === header2.value
          );
          return positionFilter1 - positionFilter2;
        });

        let filteredHeaders = sortedHeaders.filter(el => !getNotVisibleHeaders(this).includes(el.value));
        filteredHeaders.forEach((el) => {
          let valPath = responseDataPath + el.value;
          let cellType = el.type ?? String;
          schema.push({
            column: el.text,
            value: item => {
              const value = this.deepGet(item, valPath);

              if (el.presentWithInExcelExport && value !== undefined) {
                return el.presentWithInExcelExport(value, item?.data ?? item);
              }

              if (!el.presentWithRequiresParamItem && (value === undefined || value === null)) {
                return '';
              }

              if (!el.presentWith) {
                return value ? cellType(value) : '';
              }

              return cellType(el.presentWith(value, item?.data ?? item));
            },
            type: cellType,
          });
        });

        this.filename = dateFormatter.getLocalDateTimeStringNow() + '_' + this.name;

        let sortOptions = {};
        for (let i = 0; i < this.sortBy.length; i++) {
          sortOptions[this.sortBy[i]] = this.sortDesc[i];
        }
        const processedFilters = cloneDeep(this.filters) ?? {};
        for (const [key, value] of Object.entries(processedFilters)) {
          if (Array.isArray(value)) {
            processedFilters[key] = value.map(v => encodeURIComponent(v)).join(',');
          }
        }        
        const mandatoryFilters = cloneDeep(this.mandatoryFilters);
        for (const [key, value] of Object.entries(mandatoryFilters ?? {})) {
          if (Array.isArray(value)) {
            mandatoryFilters[key] = value.map(v => encodeURIComponent(v)).join(',');
          }
        }
        if (this.search)
          processedFilters['search'] = this.search

        let bulkIds = getBulkIds(this);
        let excludeIdsSelectionMode = getExcludeIds(this);

        if (bulkIds !== undefined && excludeIdsSelectionMode !== undefined) {
          if (excludeIdsSelectionMode && bulkIds.length > 0) {
            processedFilters.excludeIds = bulkIds.join(',')
          } else if (!excludeIdsSelectionMode && bulkIds.length > 0) {
            processedFilters.ids = bulkIds.join(',')
          }
        }

        axios
          .get('/rest/v4/' + this.path, {
            params: {
              ...processedFilters,
              ...mandatoryFilters,
              sort: restUtils.convertSortOptionsToQueryParam(sortOptions),
              page: 0,
              size: 9999
            },
          })
          .then((response) => {
            exportToCsv(response.data.data, schema, this.filename + '.csv');
          })
          .catch((error) => {
            window.console.error(error);
          })
          .finally(() => {
            this.csvExportLoading = false
          });
      },
    },
  }
}